import { useState } from 'react';
import type { ReactElement } from 'react';
import { Button, Input, Modal, Textarea, cn, useI18n } from '@xdocs/ui';
import { useAdminT } from './useAdminT.js';
import styles from './TypedConfirmDialog.module.css';

export interface TypedConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body: string;
  /** The exact string the caller must retype to enable the confirm button
   * (ux.md "High-stakes/irreversible → typed confirmation", not a bare
   * confirm button) — here the target user's email (F58's "Enter view-as"). */
  confirmText: string;
  typedFieldLabel: string;
  typedFieldHint: string;
  confirmLabel: string;
  onConfirm: (reason: string) => void | Promise<void>;
  loading?: boolean;
  /** `| undefined` kept explicit (exactOptionalPropertyTypes) — see
   * `AdminConfirmDialog.tsx`'s identical field for the rationale. */
  error?: string | undefined;
}

/**
 * High-stakes typed-confirmation dialog (ux.md §Destructive actions) for F58
 * "Enter view-as" — a REGULAR named-asset confirm isn't enough for opening
 * an impersonated session, so this additionally requires retyping the exact
 * target email before the confirm button enables, on top of the same
 * mandatory audit-reason field every admin mutation carries.
 */
export function TypedConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmText,
  typedFieldLabel,
  typedFieldHint,
  confirmLabel,
  onConfirm,
  loading = false,
  error,
}: TypedConfirmDialogProps): ReactElement {
  const { t } = useI18n();
  const { t: tAdmin } = useAdminT();
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const isBusy = loading || submitting;
  const matches = typed === confirmText;

  function close(): void {
    setTyped('');
    setReason('');
    setReasonError(null);
    onOpenChange(false);
  }

  async function handleConfirm(): Promise<void> {
    if (!matches) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError(tAdmin('admin:userDetail.confirm.reasonRequired'));
      return;
    }
    setReasonError(null);
    setSubmitting(true);
    try {
      await onConfirm(trimmed);
      setTyped('');
      setReason('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={close} title={title} size="sm">
      <p className={cn(styles['body'])}>{body}</p>
      <Textarea
        label={tAdmin('admin:impersonation.confirm.reasonLabel')}
        required
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        {...(reasonError ? { error: reasonError } : {})}
      />
      <Input
        label={typedFieldLabel}
        hint={typedFieldHint}
        required
        value={typed}
        onChange={(event) => setTyped(event.target.value)}
        autoComplete="off"
      />
      {error && (
        <p role="alert" className={cn(styles['error'])}>
          {error}
        </p>
      )}
      <div className={cn(styles['actions'])}>
        <Button variant="secondary" onClick={close} disabled={isBusy}>
          {t('common:action.cancel')}
        </Button>
        <Button variant="primary" state={isBusy ? 'loading' : 'idle'} disabled={!matches} onClick={() => void handleConfirm()}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
